import {useState} from 'react';

import {createDataTableConfig} from '@/lib/helpers/config';

import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

type MetrcTab = 'items' | 'deliveries' | 'packages' | 'transfers';

function AdminMetrcPage() {
  // Hooks
  const [activeTab, setActiveTab] = useState<MetrcTab>('items');

  // Items Table Config
  const itemsTableConfig = createDataTableConfig({
    table: 'Metrc_Items',
    title: 'Metrc Items',
    columns: [
      'id',
      'name',
      'description',
      'product_category_name',
      'strain_name',
      'unit_thc_percent',
      'unit_cbd_percent',
    ],
    columnsDisplay: [
      {header: 'Name', field: 'name'},
      {header: 'Category', field: 'product_category_name'},
      {header: 'Strain', field: 'strain_name'},
      {
        header: 'THC %',
        field: 'unit_thc_percent',
        render: item => item.unit_thc_percent?.toFixed(2) ?? 'N/A',
      },
      {
        header: 'CBD %',
        field: 'unit_cbd_percent',
        render: item => item.unit_cbd_percent?.toFixed(2) ?? 'N/A',
      },
    ],
    customFilterButtons: [
      {
        type: 'dropdown',
        label: 'Category',
        color: 'primary',
        options: [
          {label: 'All', value: 'all'},
          {label: 'Flower', value: 'flower'},
          {label: 'Concentrate', value: 'concentrate'},
          {label: 'Edible', value: 'edible'},
        ],
        onChange: () => null,
      },
    ],
    sortOptions: [
      {
        label: 'Created Date',
        column: 'id',
        direction: 'asc',
      },
    ],
  });

  // Transfers Table Config
  const transfersTableConfig = createDataTableConfig({
    table: 'Metric_Transfer',
    title: 'Metrc Transfers',
    columns: ['manifest_number', 'created_date_time', 'shipper_facility_name', 'received_by_facility_name'],
    columnsDisplay: [
      {header: 'Manifest #', field: 'manifest_number'},
      {
        header: 'Created Date',
        field: 'created_date_time',
        render: transfer => new Date(transfer.created_date_time).toLocaleDateString(),
      },
      {header: 'Shipper', field: 'shipper_facility_name'},
      {header: 'Receiver', field: 'received_by_facility_name'},
    ],
    sortOptions: [
      {
        label: 'Created Date',
        column: 'created_date_time',
        direction: 'asc',
      },
    ],
  });

  // Deliveries Table Config
  const deliveriesTableConfig = createDataTableConfig({
    table: 'Metric_Delivery',
    title: 'Metrc Deliveries',
    columns: ['fk_metrc_transfer_id', 'actual_arrival_date_time', 'recipient_facility_name'],
    columnsDisplay: [
      {header: 'Manifest #', field: 'fk_metrc_transfer_id'},
      {
        header: 'Arrival Date',
        field: 'actual_arrival_date_time',
        render: delivery =>
          delivery.actual_arrival_date_time ? new Date(delivery.actual_arrival_date_time).toLocaleDateString() : 'N/A',
      },
      {header: 'Receiver', field: 'recipient_facility_name'},
    ],
    sortOptions: [
      {
        label: 'Arrival Date',
        column: 'actual_arrival_date_time',
        direction: 'desc',
      },
    ],
  });

  // Packages Table Config
  const packagesTableConfig = createDataTableConfig({
    table: 'Metric_Packages',
    title: 'Metrc Packages',
    columns: [
      'fk_metrc_delivery_id',
      'package_label',
      'metrc_item_weight',
      'gross_unit_of_weight_name',
      'use_by_date',
      'product_name',
      'product_category_name',
      'product_strain_name',
    ],
    columnsDisplay: [
      {header: 'Manifest #', field: 'fk_metrc_delivery_id'},
      {
        header: 'Use By Date',
        field: 'use_by_date',
      },
      {header: 'Weight', field: 'metrc_item_weight'},
      {header: 'Weight Unit', field: 'gross_unit_of_weight_name'},
    ],
    sortOptions: [
      {
        label: 'Use By Date',
        column: 'use_by_date',
        direction: 'desc',
      },
    ],
  });

  // Render the active table based on the selected tab
  const renderActiveTable = () => {
    switch (activeTab) {
      case 'items':
        return <DataTable {...itemsTableConfig} />;
      case 'transfers':
        return <DataTable {...transfersTableConfig} />;
      case 'deliveries':
        return <DataTable {...deliveriesTableConfig} />;
      case 'packages':
        return <DataTable {...packagesTableConfig} />;
      default:
        return <DataTable {...itemsTableConfig} />;
    }
  };

  return (
    <Page currentPage="Metrc Data">
      {/* Tab Navigation */}
      <div className="mb-4 flex space-x-2">
        {(['items', 'deliveries', 'packages', 'transfers'] as MetrcTab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`ti-btn ${
              activeTab === tab
                ? 'bg-primary text-white'
                : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
            } capitalize`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Active Table */}
      {renderActiveTable()}
    </Page>
  );
}

export default AdminMetrcPage;
